
/*
  Image Compare (drag slider + media-compare scroll driver).
  Vendor must define global ImageCompare; base.js may inject scripts out of order, so we retry after load.
*/
let imageCompareVendorRetryScheduled = false

const scheduleImageCompareVendorRetry = () => {
  if (typeof ImageCompare !== 'undefined') return
  if (imageCompareVendorRetryScheduled) return

  imageCompareVendorRetryScheduled = true
  window.addEventListener(
    'load',
    () => {
      imageCompareVendorRetryScheduled = false
      initMountedImageCompare(document)
    },
    { once: true }
  )
}

const initMountedImageCompare = (root = document) => {
  if (typeof ImageCompare === 'undefined') return

  const sliders = root.querySelectorAll('.image-compare-slider')

  for (const slider of sliders) {
    if (slider.dataset.imageCompareMounted === 'true') continue

    slider.dataset.imageCompareMounted = 'true'

    const instance = new ImageCompare(slider, {
      controlColor: slider.dataset.controlColor,
      controlShadow: true,
      addCircle: slider.dataset.addCircle === 'true',
      addCircleBlur: false,
      showLabels: slider.dataset.showLabels === 'true',
      labelOptions: {
        before: slider.dataset.labelBefore,
        after: slider.dataset.labelAfter,
        onHover: slider.dataset.labelOnHover === 'true'
      },
      smoothing: slider.dataset.smoothing === 'true',
      smoothingAmount: Number(slider.dataset.smoothingAmount),
      hoverStart: slider.dataset.hoverStart === 'true',
      verticalMode: slider.dataset.verticalMode === 'true',
      startingPoint: Number(slider.dataset.startingPoint),
      fluidMode: false
    })

    instance.mount()
  }
}

class McmpScroll extends HTMLElement {
  constructor() {
    super()

    this.frame = null
    this.isObserving = false

    this.handleIntersect = this.handleIntersect.bind(this)
    this.handleScroll = this.handleScroll.bind(this)
    this.handleResize = this.handleResize.bind(this)
  }

  connectedCallback() {
    if (this.dataset.mcmpReady === 'true') {
      this.refresh()
      return
    }

    this.dataset.mcmpReady = 'true'
    this.section = this.closest('[data-mcmp-root]') || this
    const sp = this.dataset.startingPoint
    const ep = this.dataset.endingPoint
    this.startingPoint = sp !== undefined && sp !== '' ? Number(sp) : 0
    this.endingPoint = ep !== undefined && ep !== '' ? Number(ep) : 100
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (this.prefersReducedMotion) {
      this.setProgress(50)
      return
    }

    this.observer = new IntersectionObserver(this.handleIntersect, {
      threshold: [0, 0.15, 0.35, 0.5, 0.75, 1]
    })

    this.observer.observe(this.section)
    this.refresh()
  }

  disconnectedCallback() {
    this.stopObserving()

    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }

    if (this.frame) {
      window.cancelAnimationFrame(this.frame)
      this.frame = null
    }
  }

  handleIntersect(entries) {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        this.startObserving()
        this.refresh()
      } else {
        this.stopObserving()
      }
    }
  }

  startObserving() {
    if (this.isObserving) return

    this.isObserving = true
    window.addEventListener('scroll', this.handleScroll, { passive: true })
    window.addEventListener('resize', this.handleResize)
  }

  stopObserving() {
    if (!this.isObserving) return

    this.isObserving = false
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.handleResize)
  }

  handleScroll() {
    if (this.frame) return

    this.frame = window.requestAnimationFrame(() => {
      this.frame = null
      this.refresh()
    })
  }

  handleResize() {
    this.refresh()
  }

  refresh() {
    const rect = this.section.getBoundingClientRect()
    const vh = window.innerHeight || document.documentElement.clientHeight
    const denom = vh + rect.height
    let t = denom > 0 ? (vh - rect.top) / denom : 0.5

    t = Math.max(0, Math.min(1, t))

    const progress = this.startingPoint + (this.endingPoint - this.startingPoint) * t

    this.setProgress(progress)
  }

  setProgress(progress) {
    const bounded = Math.max(0, Math.min(100, progress))
    this.style.setProperty('--mcmp-clip', `${bounded}%`)
  }
}

if (!customElements.get('mcmp-scroll')) {
  customElements.define('mcmp-scroll', McmpScroll)
}

const initImageCompareSections = (root = document) => {
  initMountedImageCompare(root)
  if (typeof ImageCompare === 'undefined') {
    scheduleImageCompareVendorRetry()
  }

  const mcmpDrivers = root.querySelectorAll('mcmp-scroll')

  for (const el of mcmpDrivers) {
    if (typeof el.refresh === 'function') {
      el.refresh()
    }
  }
}

window.initImageCompareSections = initImageCompareSections

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initImageCompareSections(document)
  }, { once: true })
} else {
  initImageCompareSections(document)
}

document.addEventListener('shopify:section:load', event => {
  initImageCompareSections(event.target)

  if (typeof ImageCompare === 'undefined' && document.readyState === 'complete') {
    const retry = () => initMountedImageCompare(document)

    setTimeout(retry, 0)
    setTimeout(retry, 100)
    setTimeout(retry, 400)
  }
})